/**
 * Campaign WebSocket Event Hook
 *
 * File: Mythbound.App/src/hooks/useCampaignWebSocket.ts
 * Created: 2026-01-06
 * Last Modified: 2026-01-06
 *
 * PURPOSE:
 * React hook for subscribing to campaign-related WebSocket events.
 * Integrates with CampaignContext to update state in real-time.
 *
 * EVENTS HANDLED:
 * - campaign.created - New campaign created
 * - campaign.updated - Campaign settings updated
 * - campaign.deleted - Campaign deleted/archived
 * - campaign.participant_added - Participant joined campaign
 * - campaign.participant_removed - Participant left campaign
 * - campaign.tier_limit_warning - Over tier limit (soft warning)
 *
 * USAGE:
 * ```tsx
 * function CampaignPage() {
 *   const { currentCampaign } = useCampaign();
 *   useCampaignWebSocket(currentCampaign?.campaign_id);
 *   // Component automatically updates on WebSocket events
 * }
 * ```
 *
 * Pipeline: WebSocket events → useCampaignWebSocket → CampaignContext → UI updates
 * Dependencies: React, websocketService, useCampaign context
 */

import { useEffect, useRef } from 'react';
import { useCampaign } from '../context/CampaignContext';
import { toastService } from '../services/toast/toastService';
import { webSocketService } from '../services/websocket/websocketService';

export const useCampaignWebSocket = (campaignId?: string) => {
  const {
    updateCampaign,
    addParticipant,
    removeParticipant,
    deleteCampaign,
    loadCampaigns
  } = useCampaign();

  // Store handlers in refs to avoid re-subscribing on every render
  const handlersRef = useRef<Record<string, (data: any) => void>>({});

  useEffect(() => {
    // Skip if no campaign ID
    if (!campaignId) return;

    /**
     * Handle campaign.created event
     * Triggered when a new campaign is created (by this user or others in shared context)
     */
    handlersRef.current['campaign.created'] = (data: any) => {
      console.log('[Campaign WS] campaign.created:', data);
      // Reload campaign list to include new campaign
      loadCampaigns();
    };

    /**
     * Handle campaign.updated event
     * Triggered when campaign settings are updated
     */
    handlersRef.current['campaign.updated'] = (data: any) => {
      console.log('[Campaign WS] campaign.updated:', data);
      if (data.campaign_id === campaignId) {
        updateCampaign(data.campaign_id, data);
      }
    };

    /**
     * Handle campaign.deleted event
     * Triggered when campaign is deleted/archived
     */
    handlersRef.current['campaign.deleted'] = (data: any) => {
      console.log('[Campaign WS] campaign.deleted:', data);
      if (data.campaign_id === campaignId) {
        deleteCampaign(data.campaign_id);
      }
    };

    /**
     * Handle campaign.participant_added event
     * Triggered when a new participant joins the campaign
     */
    handlersRef.current['campaign.participant_added'] = (data: any) => {
      console.log('[Campaign WS] campaign.participant_added:', data);
      if (data.campaign_id === campaignId) {
        addParticipant({
          user_id: data.user_id,
          screen_name: data.screen_name || 'Unknown',
          role: data.role || 'player',
          character_id: data.character_id,
          character_name: data.character_name,
          joined_at: data.joined_at
        });

        toastService.info(`${data.screen_name || 'A player'} joined the campaign`);
      }
    };

    /**
     * Handle campaign.participant_removed event
     * Triggered when a participant leaves the campaign
     */
    handlersRef.current['campaign.participant_removed'] = (data: any) => {
      console.log('[Campaign WS] campaign.participant_removed:', data);
      if (data.campaign_id === campaignId) {
        removeParticipant(data.user_id);

        toastService.info(`${data.screen_name || 'A player'} left the campaign`);
      }
    };

    /**
     * Handle campaign.tier_limit_warning event
     * Triggered when campaign exceeds tier participant limit (soft warning)
     */
    handlersRef.current['campaign.tier_limit_warning'] = (data: any) => {
      console.log('[Campaign WS] campaign.tier_limit_warning:', data);
      if (data.campaign_id === campaignId) {
        toastService.warning(
          `Campaign over tier limit (${data.current_count}/${data.max_participants}). Consider upgrading to Premium.`,
          6000 // Show warning longer
        );
        console.warn(
          `Campaign over tier limit: ${data.current_count}/${data.max_participants}`,
          data.message
        );
      }
    };

    // Subscribe to all events
    Object.entries(handlersRef.current).forEach(([event, handler]) => {
      webSocketService.on(event, handler);
    });

    // Cleanup: unsubscribe from all events
    return () => {
      Object.entries(handlersRef.current).forEach(([event, handler]) => {
        webSocketService.off(event, handler);
      });
    };
  }, [campaignId, updateCampaign, addParticipant, removeParticipant, deleteCampaign, loadCampaigns]);
};

